import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import config from '../config';
import { RecalculationProgress, ActiveJob } from 'types/RecalculationProgress';
import { useApiCalls } from 'helpers/api/useApiCalls';
import { useApiCalls as api } from 'helpers/api/useApiCalls';
import { act } from 'react-dom/test-utils';

interface RecalculationHubContextType {
    connection: HubConnection | null;
    isConnected: boolean;
    activeJobs: Map<string, RecalculationProgress>;
    joinJobGroup: (jobId: string) => Promise<void>;
    leaveJobGroup: (jobId: string) => Promise<void>;
    refreshActiveJobs: () => Promise<void>;
    reconnect: () => Promise<void>;
}

export const RecalculationHubContext = createContext<RecalculationHubContextType | undefined>(undefined);

export const useRecalculationHub = () => {
    const context = useContext(RecalculationHubContext);
    if (!context) {
        throw new Error('useRecalculationHub must be used within RecalculationHubProvider');
    }
    return context;
};

interface RecalculationHubProviderProps {
    children: React.ReactNode;
}

export const RecalculationHubProvider: React.FC<RecalculationHubProviderProps> = ({ children }) => {
    const [connection, setConnection] = useState<HubConnection | null>(null);
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const [forceReconnect, setForceReconnect] = useState<number | null>(null);
    const [activeJobs, setActiveJobs] = useState<Map<string, RecalculationProgress>>(new Map());
    const connectionRef = useRef<HubConnection | null>(null);
    const apiCalls = useApiCalls();
    const activeUser = api().active();

    // Unirse a un grupo de trabajo
    const joinJobGroup = useCallback(async (jobId: string) => {
        if (connectionRef.current && isConnected) {
            try {
                console.log('[SignalR] 👥 Uniéndose al grupo:', `job-${jobId}`);
                await connectionRef.current.invoke('JoinJobGroup', jobId);
                console.log('[SignalR] ✅ Unido exitosamente al grupo:', `job-${jobId}`);
            } catch (error) {
                console.error('[SignalR] ❌ Error uniéndose al grupo', `job-${jobId}:`, error);
            }
        } else {
            console.warn('[SignalR] ⚠️ No se puede unir al grupo: conexión no disponible');
        }
    }, [isConnected]);

    // Salir de un grupo de trabajo
    const leaveJobGroup = useCallback(async (jobId: string) => {
        if (connectionRef.current && isConnected) {
            try {
                console.log('[SignalR] 👋 Saliendo del grupo:', `job-${jobId}`);
                await connectionRef.current.invoke('LeaveJobGroup', jobId);
                console.log('[SignalR] ✅ Salido exitosamente del grupo:', `job-${jobId}`);
            } catch (error) {
                console.error('[SignalR] ❌ Error saliendo del grupo', `job-${jobId}:`, error);
            }
        }
    }, [isConnected]);

    // Refrescar trabajos activos y unirse a sus grupos
    // Usar useRef para mantener una referencia estable y evitar recreaciones
    const refreshActiveJobsRef = useRef<() => Promise<void>>();
    
    const refreshActiveJobs = useCallback(async () => {
        try {
            // Usar el nuevo endpoint que filtra por cliente automáticamente
            const response = await apiCalls.SignalR.getActiveJobsByClient();
            const jobs: ActiveJob[] = response?.data || [];
            
            const userClientId = activeUser?.client?.id;
            const userId = activeUser?.auth0Id;
            
            console.log('[SignalR] 🔍 Trabajos activos del cliente:', {
                totalJobs: jobs.length,
                userClientId,
                userId,
                jobIds: jobs.map(j => j.jobId)
            });

            // Unirse a los grupos de trabajos activos solo si hay conexión
            // Verificar connectionRef directamente para evitar problemas de timing con el estado
            if (connectionRef.current && connectionRef.current.state === 'Connected') {
                for (const job of jobs) {
                    // Unirse a trabajos en procesamiento o en cola
                    if (job.status === 'processing' || job.status === 'enqueued' || job.status === 'started') {
                        await joinJobGroup(job.jobId);
                    }
                }
            } else {
                // Si no hay conexión, intentar conectar primero
                console.log('[SignalR] ⚠️ No hay conexión activa, los trabajos se unirán cuando se establezca la conexión');
            }
        } catch (error) {
            console.error('[SignalR] ❌ Error refreshing active jobs:', error);
            // Fallback al endpoint anterior si el nuevo no está disponible
            try {
                const response = await apiCalls.SignalR.getActiveJobs();
                const jobs: ActiveJob[] = response?.data || [];
                
                const userClientId = activeUser?.client?.id;
                const userId = activeUser?.auth0Id;
                
                const filteredJobs = jobs.filter(job => {
                    if (job.clientId != null && userClientId != null) {
                        return job.clientId === userClientId;
                    }
                    if (job.userId != null && userId != null) {
                        return job.userId === userId;
                    }
                    return false; // Solo mostrar trabajos del cliente/usuario
                });

                if (connectionRef.current && connectionRef.current.state === 'Connected') {
                    for (const job of filteredJobs) {
                        if (job.status === 'processing' || job.status === 'enqueued' || job.status === 'started') {
                            await joinJobGroup(job.jobId);
                        }
                    }
                }
            } catch (fallbackError) {
                console.error('[SignalR] ❌ Error en fallback:', fallbackError);
            }
        }
    }, [apiCalls, activeUser, joinJobGroup, isConnected]);
    
    // Actualizar la referencia cuando refreshActiveJobs cambie
    refreshActiveJobsRef.current = refreshActiveJobs;

    // Método para reconectar manualmente
    const reconnect = useCallback(async () => {
        console.log('[SignalR] 🔄 Reconexión manual iniciada');
        
        // Detener la conexión actual si existe
        if (connectionRef.current) {
            try {
                await connectionRef.current.stop();
                console.log('[SignalR] ✅ Conexión anterior detenida');
            } catch (error) {
                console.warn('[SignalR] ⚠️ Error al detener conexión anterior:', error);
            }
            connectionRef.current = null;
        }
        
        // Limpiar el estado
        setConnection(null);
        setIsConnected(false);
        
        // Forzar una nueva conexión incrementando forceReconnect
        setForceReconnect(prev => (prev === null ? 1 : prev + 1));
    }, []);

    // Inicializar conexión SignalR solo si el usuario está autenticado
    useEffect(() => {
        if (connectionRef.current && !forceReconnect) {
            return;
        }
        // Solo conectar si hay un usuario activo
        if (!(activeUser?.token)) {
            console.log('[SignalR] ⏸️ Esperando usuario activo para conectar al hub');
            setForceReconnect(forceReconnect === null ? 1 : forceReconnect + 1); // Intentar reconectar más tarde
            return;
        }
        else {
            setForceReconnect(null);
        }

        // Construir hubUrl correctamente: remover solo /api del final de la URL base
        // https://api.xxxx.escoinversiones.com/api -> https://api.xxxx.escoinversiones.com/recalculationHub
        const baseUrl = config.REACT_APP_API_URL.endsWith('/api') 
            ? config.REACT_APP_API_URL.slice(0, -4) // Remover '/api' (4 caracteres)
            : config.REACT_APP_API_URL;
        const hubUrl = `${baseUrl}/recalculationHub`;
        
        const newConnection = new HubConnectionBuilder()
            .withUrl(hubUrl, {
                withCredentials: true,
                accessTokenFactory: () => {
                    // Obtener el token del usuario activo
                    const token = activeUser?.token;
                    if (!token) {
                        console.warn('[SignalR] ⚠️ No hay token disponible para la conexión');
                        return Promise.resolve('');
                    }
                    return Promise.resolve(token);
                },
            })
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (retryContext) => {
                    if (retryContext.previousRetryCount < 10) {
                        // Intentar reconectar rápidamente las primeras 10 veces
                        return 2000; // 2 segundos
                    } else {
                        // Después de 10 intentos, esperar más tiempo
                        return 5000; // 5 segundos
                    }
                }
            })
            // LogLevel.Debug muestra todos los mensajes de transporte (WebSocket frames)
            // LogLevel.Information muestra eventos importantes
            .configureLogging(process.env.NODE_ENV === 'development' ? LogLevel.Debug : LogLevel.Information)
            .build();

        // Configurar handlers
        newConnection.on('RecalculationProgress', (progress: RecalculationProgress) => {
            console.log('[SignalR] 📨 RecalculationProgress recibido:', {
                jobId: progress.jobId,
                status: progress.status,
                progress: `${progress.processedDates}/${progress.totalDates} (${progress.progressPercentage.toFixed(2)}%)`,
                currentDate: progress.currentDate,
                message: progress.message,
                timestamp: progress.timestamp
            });
            setActiveJobs(prev => {
                const updated = new Map(prev);
                // Convertir fechas de string a Date si vienen como string
                if (progress.currentDate && typeof progress.currentDate === 'string') {
                    progress.currentDate = new Date(progress.currentDate);
                }
                if (typeof progress.timestamp === 'string') {
                    progress.timestamp = new Date(progress.timestamp);
                }
                updated.set(progress.jobId, progress);
                return updated;
            });
        });

        // También escuchar el evento genérico JobProgress por si se usa el hub genérico en el futuro
        newConnection.on('JobProgress', (progress: any) => {
            // Convertir a formato RecalculationProgress si es necesario
            if (progress && progress.jobId) {
                const recalculationProgress: RecalculationProgress = {
                    jobId: progress.jobId,
                    userId: progress.userId,
                    clientId: progress.clientId,
                    status: progress.status,
                    currentDate: progress.currentDate ? new Date(progress.currentDate) : null,
                    totalDates: progress.totalItems || progress.totalDates || 0,
                    processedDates: progress.processedItems || progress.processedDates || 0,
                    progressPercentage: progress.progressPercentage || 0,
                    message: progress.message,
                    timestamp: progress.timestamp ? new Date(progress.timestamp) : new Date()
                };
                
                setActiveJobs(prev => {
                    const updated = new Map(prev);
                    updated.set(recalculationProgress.jobId, recalculationProgress);
                    return updated;
                });
            }
        });

        newConnection.onclose((error) => {
            console.log('[SignalR] 🔌 Conexión cerrada', error ? `Error: ${error.message}` : '');
            setIsConnected(false);
        });

        newConnection.onreconnecting((error) => {
            console.log('[SignalR] 🔄 Reconectando...', error ? `Error: ${error.message}` : '');
            setIsConnected(false);
        });

        newConnection.onreconnected((connectionId) => {
            console.log('[SignalR] ✅ Reconectado exitosamente. ConnectionId:', connectionId);
            setIsConnected(true);
            // Reconectar a los grupos de trabajos activos usando la referencia
            if (refreshActiveJobsRef.current) {
                refreshActiveJobsRef.current();
            }
        });

        // Iniciar conexión - permitir que falle sin bloquear la app
        console.log('[SignalR] 🚀 Iniciando conexión a:', hubUrl);
        newConnection.start()
            .then(async () => {
                console.log('[SignalR] ✅ Conectado exitosamente. ConnectionId:', newConnection.connectionId);
                setIsConnected(true);
                setConnection(newConnection);
                connectionRef.current = newConnection;
                
                // Cargar trabajos activos del cliente al conectar automáticamente
                // Esto asegura que el usuario vea los trabajos en progreso al iniciar la aplicación
                if (refreshActiveJobsRef.current) {
                    console.log('[SignalR] 🔄 Cargando trabajos activos del cliente al conectar...');
                    await refreshActiveJobsRef.current();
                }
            })
            .catch((error) => {
                // Error silencioso: loguear pero no bloquear la aplicación
                console.warn('[SignalR] ⚠️ Error conectando al hub (no bloqueante):', {
                    message: error?.message || 'Unknown error',
                    error: error?.toString?.()
                });
            });

        return () => {
            if (connectionRef.current) {
                connectionRef.current.stop().catch(() => {});
            }
        };
    }, [activeUser?.token, forceReconnect]);

    const value: RecalculationHubContextType = {
        connection,
        isConnected,
        activeJobs,
        joinJobGroup,
        leaveJobGroup,
        refreshActiveJobs,
        reconnect,
    };

    return (
        <RecalculationHubContext.Provider value={value}>
            {children}
        </RecalculationHubContext.Provider>
    );
};
